import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { IonButton, IonItem, IonLabel, IonSkeletonText, IonText, IonChip } from '@ionic/react';
import dayjs from 'dayjs';
import { IAppointment } from './IAppointment';
import { AppointmentsState, NEW_APPOINTMENT_ID } from './AppointmentsState';
import { createUseStyles } from 'react-jss';
import { useSubscription } from '@meraki-internal/state';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { ISupportGroupKey } from '../caremap/ICareMap';
import { ISO8601 } from '../support/ISO8601';
import { AddAppointmentButton } from './AddAppointmentButton';
import { DrawerSectionHeading } from '../components/DrawerSectionHeading';
import { Text } from '../components/Text';
import { PaywallAwareButton } from '../components/PaywallAwareButton';
import { RecordingState } from './recording/RecordingState';
import { AIIcon } from '../theme/Icons';
import { RecordingInfo } from './recording/RecordingInfo';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { AppointmentGrouper } from './AppointmentGrouper';

const useStyles = createUseStyles({
    dateHeading: {
        padding: 6,
        fontWeight: 500,
        textTransform: 'uppercase',
        background: 'var(--ion-color-step-50)',
        color: 'var(--ion-color-step-600, #666666)'
    },

    todayHeading: {
        color: 'var(--ion-color-secondary)',
    },

    appointment: {
        gap: 8,
        display: 'flex'
    },

    times: {
        fontSize: 16,
        fontWeight: '500',
        textAlign: 'end',
        color: 'var(--ion-color-step-600, #666666)'
    },

    endTime: {
        fontSize: 14,
        paddingTop: 2.2
    },

    details: {
        flexGrow: 1
    },

    title: {
        fontWeight: 500
    },

    location: {
        color: 'var(--ion-color-step-600, #666666)'
    },

    moreBtnWrapper: {
        display: 'flex',
        justifyContent: 'space-around',

        // push this into the DrawerSection bottom margin
        marginBottom: -24
    },

    loadingMask: {
        width: '100%',
        position: 'absolute',
        paddingInlineStart: 'var(--padding-start)',
        paddingInlineEnd: 'var(--padding-end)',
        left: 0,
        right: 0,
        background: 'var(--ion-background-color, #fff)',
        zIndex: 10
    },

    tagsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        marginTop: 4,
        marginBottom: 4
    }
});

// Same color logic as TagsField
const getTagColor = (tag: string) => {
    const tagColorOptions = [
        '#4A8FA8', // Teal (primary theme equivalent)
        '#8E5FC7', // Purple (tertiary theme equivalent)
        '#D45A2A', // Orange (secondary theme equivalent)
        '#4A8F6B', // Green (success theme equivalent)
        '#5A72C7', // Blue
        '#C768A3', // Pink
        '#3E4A5C', // Dark blue-gray
        '#D47A47', // Orange-brown
        '#C73E3E'  // Red
    ];

    let hash = 0;
    for (let i = 0; i < tag.length; i++) {
        const char = tag.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    const colorIndex = Math.abs(hash) % tagColorOptions.length;
    const color = tagColorOptions[colorIndex];

    return {
        backgroundColor: `${color}1A`, // 10% opacity
        color: color,
        border: `1px solid ${color}`
    };
};

export const AppointmentListItemTags: FC<{ tags?: string[] }> = ({ tags }) => {
    const classes = useStyles();

    if (!tags || tags.length === 0) {
        return null;
    }

    const maxTagsToShow = 3;
    const visibleTags = tags.slice(0, maxTagsToShow);
    const remainingCount = tags.length - maxTagsToShow;

    return (
        <div className={classes.tagsContainer}>
            {visibleTags.map(tag => (
                <IonChip key={tag} style={getTagColor(tag)}>
                    <IonLabel style={{ fontSize: '12px' }}>{tag}</IonLabel>
                </IonChip>
            ))}
            {remainingCount > 0 && (
                <IonChip style={{ backgroundColor: 'var(--ion-color-step-100)', color: 'var(--ion-color-step-600)' }}>
                    <IonLabel style={{ fontSize: '12px' }}>+{remainingCount} more</IonLabel>
                </IonChip>
            )}
        </div>
    );
};

export type IAppointmentsListProps = {
    showCount?: number;
    hideHeading?: boolean;
    'data-type'?: string;
    minDate?: ISO8601;
    numDaysToShow?: number;
    showViewMore?: boolean;
    supportMemberId?: string;
    supportGroupKey?: ISupportGroupKey;
    editAppointment: (apptId: string) => void;
    viewMore?: () => void;
    contentRef?: React.RefObject<HTMLIonContentElement>;
    heightOffset?: number;
    ignoreHasScrolled?: boolean;
    showEmptyToday?: boolean;
    customEmptyMsg?: String | ReactNode;
    onViewRecording?: (noteId: string) => void;
    filteredAppointments?: IAppointment[];
};

export const AppointmentsList: FC<IAppointmentsListProps> = ({
    showCount,
    hideHeading = false,
    'data-type': dataType = 'appointments-list',
    minDate,
    numDaysToShow,
    showViewMore = false,
    supportMemberId,
    supportGroupKey,
    editAppointment,
    viewMore = () => {},
    contentRef,
    heightOffset = 0,
    ignoreHasScrolled = false,
    showEmptyToday = false,
    customEmptyMsg,
    onViewRecording = (noteId) => {},
    filteredAppointments
}) => {
    const classes = useStyles();

    const appointmentsState = useInstance(AppointmentsState);
    useSubscription(() => appointmentsState);

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    // so the list re-renders when the notes load
    const careMapNotesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => careMapNotesFactory);

    const todayGroupRef = useRef<HTMLDivElement>(null);

    const [mounted, setMounted] = useState(false);
    const [hasScrolled, setHasScrolled] = useState<boolean>(ignoreHasScrolled);


    const formatDateHeading = (date: string) => {
        const dayjsDate = dayjs(date);
        const today = dayjsDate.isToday();
        const format =  `${today ? '' : 'dddd, '}MMM D${dayjsDate.year() !== dayjs().year() ? ', YYYY' : ''}`;
        return `${today ? 'Today, ' : ''}${dayjsDate.format(format)}`;
    };

    const formatAppointmentTime = (appt: IAppointment, group: string, isStart: boolean) => {
        let format = 'MMM D';
        const dateToFormat = dayjs(isStart ? appt.start : appt.end);
        const isSameDay = dateToFormat.isSame(group, 'day');

        if (isSameDay) {
            format = 'h:mma';
        }

        return dateToFormat.format(format);
    };

    // get appropriate appointments and sort descending chronologically
    let appointments = filteredAppointments || (
        supportGroupKey ? appointmentsState.getAppointmentsForSupportGroup(supportGroupKey) :
            supportMemberId ? appointmentsState.getAppointmentsForSupportMember(supportMemberId) : appointmentsState.state.appointments
    );
    appointments = (appointments || []).sort((n1, n2) => n1.start.localeCompare(n2.start));

    const hasAppointments = appointments.length > 0;

    // group appointments using AppointmentGrouper
    const appointmentGrouper = new AppointmentGrouper(appointments, minDate, showEmptyToday);
    const groupedAppointments = appointmentGrouper.group();

    const canAdd = appointmentsState.canAdd();
    const loaded = appointmentsState.state.loaded;
    const hasVisibleAppointments: boolean = Object.keys(groupedAppointments).length > 0;

    useEffect(() => {
        setMounted(true);
        return () => setMounted(false);
    }, []);

    useEffect(() => {
        if (mounted && loaded && !ignoreHasScrolled) {

            // need a small timeout, otherwise care maps with very few appointments can finish
            // loading before page layout is done, causing offset below to fail include fixed header
            setTimeout(() => {
                const pageContent = contentRef?.current;
                if (pageContent) {
                    const offset = todayGroupRef.current?.offsetTop;
                    if (offset) {
                        pageContent.scrollToPoint(0, offset - heightOffset);
                    }
                    setHasScrolled(true);
                }
            }, 100);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentRef?.current, heightOffset, ignoreHasScrolled, loaded, mounted]);

    return (
        <div
            data-type={dataType}
            style={{width: '100%', height: `calc(100% - ${heightOffset}px)`, paddingBottom: 16}}
        >
            {!hideHeading && (
                <DrawerSectionHeading text="Appointments">
                    <AddAppointmentButton
                        useIcon
                        onClick={editAppointment}
                        canAdd={canAdd}
                    />
                </DrawerSectionHeading>
            )}

            {!hasVisibleAppointments && loaded && (
                customEmptyMsg || (
                    <IonText color="medium" style={{paddingLeft: 12}}>
                        No upcoming appointments
                    </IonText>
                )
            )}

            <div
                className={classes.loadingMask}
                style={{
                    top: heightOffset,
                    height: `calc(100% + ${heightOffset}px)`,
                    display: loaded && hasScrolled ? 'none' : 'block'
                }}
            >
                <div>
                    <IonSkeletonText animated style={{'--border-radius': '8px', height: 31}}></IonSkeletonText>
                    <div style={{paddingLeft: 32, paddingTop: 1}}>
                        <IonSkeletonText animated style={{'--border-radius': '8px', height: 24}}></IonSkeletonText>
                        <IonSkeletonText animated style={{'--border-radius': '8px', height: 24, marginTop: 10}}></IonSkeletonText>
                    </div>
                </div>
            </div>

            <div style={{height: `calc(100% - ${heightOffset}px)`}}>
                <div>
                    {hasVisibleAppointments && Object.keys(groupedAppointments).sort().map((date, idx) => {
                        const isTodayGroup = dayjs(date).isToday();

                        // only show the number of days we want
                        if (idx >= numDaysToShow!) {
                            return null;
                        }

                        return (
                            <div
                                key={date}
                                data-type="appointments-date-heading"
                                ref={isTodayGroup ? todayGroupRef : undefined}
                            >
                                <div className={`${classes.dateHeading} ${isTodayGroup ? classes.todayHeading : ''}`}>
                                    {formatDateHeading(date)}
                                </div>

                                {isTodayGroup && groupedAppointments[date].length === 0 ? (
                                    <IonItem
                                        lines="none"
                                    >
                                        <IonLabel class="ion-text-wrap">
                                            <Text>
                                                <div style={{display: 'flex', alignItems: 'center'}}>
                                                    No appointments today.
                                                    {canAdd &&
                                                        <PaywallAwareButton
                                                            fill="clear"
                                                            triggerSource="add appointment"
                                                            requiresInnerhivePlus={appointmentsState.addAppointmentRequiresInnerhivePlus}
                                                            onClick={() => editAppointment(NEW_APPOINTMENT_ID)}
                                                        >
                                                            <span style={{fontSize: 16, fontWeight: 'normal'}}>Add one.</span>
                                                        </PaywallAwareButton>
                                                    }
                                                </div>
                                            </Text>
                                        </IonLabel>
                                    </IonItem>
                                ) : (
                                    groupedAppointments[date].map((appt, idx2) => {
                                        // only show the number of appointments (showCount) we want
                                        if (idx2 >= showCount!) {
                                            return null;
                                        }

                                        const note = recordingState.findNoteForAppointment(appt.appointmentId);
                                        const recording = note?.otherAttributes?.recording;

                                        return (
                                            <IonItem
                                                button
                                                detail
                                                lines="none"
                                                key={appt.appointmentId}
                                                onClick={() => editAppointment(appt.appointmentId)}
                                                data-type="appointment"
                                            >
                                                <IonLabel class="ion-text-wrap">
                                                    <div className={classes.appointment}>
                                                        <div className={classes.times}>
                                                            <div data-type="start">{formatAppointmentTime(appt, date, true)}</div>
                                                            <div data-type="end" className={classes.endTime}>{formatAppointmentTime(appt, date, false)}</div>
                                                        </div>
                                                        <div className={classes.details}>
                                                            <div data-type="title" className={classes.title}>{appt.title}</div>
                                                            <div data-type="location" className={classes.location}>{appt.location}</div>
                                                            <AppointmentListItemTags tags={appt.tags} />
                                                        </div>
                                                    </div>
                                                    {recording && recording.status === 'summarized' && (
                                                        <div style={{marginTop: 4, display: 'flex', justifyContent: 'space-between'}}>
                                                            <RecordingInfo recording={recording} hideStatus />
                                                            <div
                                                                style={{display: 'flex', alignItems: 'center'}}
                                                                onClick={async (e: React.MouseEvent) => {
                                                                    e.stopPropagation();
                                                                    onViewRecording(note.noteId || '');
                                                                }}
                                                            >
                                                                <span data-type="view-summary-btn" style={{color: 'var(--ion-color-primary)', fontSize: 14}}>
                                                                    View Summary
                                                                </span>&nbsp;<AIIcon color="primary" />
                                                            </div>
                                                        </div>
                                                    )}
                                                </IonLabel>
                                            </IonItem>
                                        );
                                    })
                                )}
                            </div>
                        );
                    })}
                </div>
                <div style={{height: `calc(100% + ${heightOffset}px)`}}></div>
            </div>

            {showViewMore && hasAppointments && (
                <div className={classes.moreBtnWrapper}>
                    <IonButton fill="clear" onClick={viewMore} data-id="view-more-appointments-button">
                        View All Appointments
                    </IonButton>
                </div>
            )}
        </div>
    );
};
